/**
 * =============================================================================
 * TemplateExportDialog — assistente de geração de template
 *
 * Passo único e enxuto: dados do template + árvore de módulos com escolha
 * item a item. Só entram configurações; nenhum cliente, contato, negócio,
 * conversa ou credencial é levado.
 *
 * @version 2.3.0
 * RELEASE: 2026-09-08 v2.3.0 - Aceita seleção inicial (atalhos "exportar este
 *          fluxo" e "exportar esta workspace"), marcando o módulo de origem e
 *          apenas os itens ligados ao item escolhido.
 * RELEASE: 2026-09-08 v2.2.0 - Itens de todos os módulos são carregados em

 *          paralelo ao abrir a janela: a quantidade aparece de imediato, sem
 *          precisar clicar em cada módulo.
 * RELEASE: 2026-09-08 v2.1.0 - Cabeçalho alinhado ao padrão editorial dos
 *          modais grandes (bg-sidebar, título único, X branco, sem subtítulo).
 * RELEASE: 2026-09-08 v2.0.0 - Janela ampla (95vw x 97vh) com seleção em
 *          mestre x detalhe, busca por item e contagem no rodapé.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (refatoração da tela), com
 *          seleção granular de itens por coleção.
 *
 * =============================================================================
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';

import { TemplateModuleSelector } from './TemplateModuleSelector';
import { useCreateTemplateExport, useTemplateModuleItemsBatch } from '@/hooks/useSystemTemplates';
import { toast } from '@/lib/toast';
import {
  TEMPLATE_MODULE_CATALOG,
  getTemplateModulesInApplyOrder,
  validateModuleSelection,
} from '@/lib/template-system/moduleCatalog';
import type { TemplateModuleKey } from '@/lib/template-system/constants';
import type { TemplateSelection, TemplateCollectionItems } from '@/lib/template-system/api';

interface TemplateExportDialogProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Avisa o id da execução criada, para acompanhar na tela. */
  onCreated: (jobId: string) => void;
  /** Módulos já marcados ao abrir (atalhos de exportação de um item só). */
  initialModules?: TemplateModuleKey[];
  /** Nome sugerido do template (ex.: nome do fluxo ou da workspace). */
  initialName?: string;
  /**
   * Monta a seleção inicial assim que os itens da conta terminam de carregar.
   * Recebe as coleções por módulo e devolve a seleção item a item.
   */
  buildInitialSelection?: (
    itemsByModule: Record<string, TemplateCollectionItems[]>,
  ) => TemplateSelection;
}

export function TemplateExportDialog({
  open,
  onOpenChange,
  onCreated,
  initialModules,
  initialName,
  buildInitialSelection,
}: TemplateExportDialogProps) {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [segmento, setSegmento] = useState('');
  const [modulos, setModulos] = useState<TemplateModuleKey[]>([]);
  const [selecao, setSelecao] = useState<TemplateSelection>({});
  // Módulo aberto na coluna de detalhe
  const [moduloAtivo, setModuloAtivo] = useState<string | null>(null);
  // Garante que a seleção inicial (atalho) seja aplicada uma única vez
  const [atalhoAplicado, setAtalhoAplicado] = useState(false);


  const exportarMutation = useCreateTemplateExport();

  /** Módulos na ordem correta de aplicação. */
  const modulosDisponiveis = useMemo(
    () =>
      getTemplateModulesInApplyOrder().map((key) => ({
        key,
        name: TEMPLATE_MODULE_CATALOG[key].name,
        description: TEMPLATE_MODULE_CATALOG[key].description,
      })),
    [],
  );

  const chavesModulos = useMemo(
    () => modulosDisponiveis.map((m) => m.key as string),
    [modulosDisponiveis],
  );

  // Carrega os itens de TODOS os módulos em paralelo assim que a janela abre,
  // para a quantidade aparecer sem precisar clicar em cada módulo.
  const { itemsByModule: itensPorModulo, loadingModules: modulosCarregando } =
    useTemplateModuleItemsBatch(chavesModulos, open);

  // Limpa o formulário sempre que a janela é fechada
  useEffect(() => {
    if (!open) {
      setNome('');
      setDescricao('');
      setSegmento('');
      setModulos([]);
      setSelecao({});
      setModuloAtivo(null);
      setAtalhoAplicado(false);
    }
  }, [open]);

  // Atalho: ao abrir vindo de "exportar este fluxo/esta workspace", já marca o
  // módulo de origem, sugere o nome e foca o detalhe nesse módulo.
  useEffect(() => {
    if (!open || !initialModules || initialModules.length === 0) return;
    setModulos(initialModules);
    setModuloAtivo(initialModules[0] as string);
    if (initialName) setNome((atual) => atual || initialName);
  }, [open, initialModules, initialName]);

  // Atalho: assim que os itens da conta chegam, aplica a seleção item a item.
  useEffect(() => {
    if (!open || atalhoAplicado || !buildInitialSelection) return;
    const alvos = initialModules ?? [];
    const prontos =
      alvos.length > 0 && alvos.every((m) => Array.isArray(itensPorModulo[m as string]));
    if (!prontos) return;
    setSelecao(buildInitialSelection(itensPorModulo));
    setAtalhoAplicado(true);
  }, [open, atalhoAplicado, buildInitialSelection, initialModules, itensPorModulo]);



  // Ao abrir a janela, já mostra o detalhe do primeiro módulo
  useEffect(() => {
    if (open && !moduloAtivo && modulosDisponiveis.length > 0) {
      setModuloAtivo(modulosDisponiveis[0].key);
    }
  }, [open, moduloAtivo, modulosDisponiveis]);


  /** Marca/desmarca um módulo inteiro. */
  const alternarModulo = (key: string) => {
    const chave = key as TemplateModuleKey;
    setModulos((atual) =>
      atual.includes(chave) ? atual.filter((m) => m !== chave) : [...atual, chave],
    );
  };

  /** Marca todos os módulos de uma vez. */
  const marcarTodos = () => setModulos(modulosDisponiveis.map((m) => m.key as TemplateModuleKey));

  /** Cria a exportação depois de validar nome e dependências. */
  const confirmar = async () => {
    if (!nome.trim()) {
      toast.error('Informe um nome para o template.');
      return;
    }
    if (modulos.length === 0) {
      toast.error('Escolha ao menos um módulo.');
      return;
    }

    const validacao = validateModuleSelection(modulos);
    if (!validacao.valid) {
      const faltantes = Array.from(
        new Set(validacao.missing.map((m) => TEMPLATE_MODULE_CATALOG[m.missingDependency].name)),
      ).join(', ');
      toast.error(`Inclua também: ${faltantes}.`);
      return;
    }

    // Só envia a seleção dos módulos realmente marcados
    const selecaoFinal: TemplateSelection = {};
    for (const chave of modulos) {
      if (selecao[chave]) selecaoFinal[chave] = selecao[chave];
    }

    try {
      const resultado = await exportarMutation.mutateAsync({
        name: nome.trim(),
        description: descricao.trim() || null,
        segment: segmento.trim() || null,
        modules: modulos,
        idempotencyKey: crypto.randomUUID(),
        selection: Object.keys(selecaoFinal).length > 0 ? selecaoFinal : null,
      });
      onCreated(resultado.job_id);
      onOpenChange(false);
      toast.success('Geração iniciada. Acompanhe o andamento na lista.');
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Não foi possível gerar o template.');
    }
  };

  // Total de itens escolhidos, mostrado no rodapé
  const totalItens = useMemo(() => {
    let soma = 0;
    for (const chave of modulos) {
      const colecoes = itensPorModulo[chave] ?? [];
      for (const colecao of colecoes) {
        const escolha = selecao[chave]?.[colecao.collection];
        // Valores que não são lista (ex.: mapas de configuração) contam a coleção inteira
        soma += Array.isArray(escolha) ? escolha.length : colecao.labels.length;
      }
    }
    return soma;
  }, [modulos, itensPorModulo, selecao]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="max-w-[95vw] w-[95vw] h-[97vh] p-0 gap-0 overflow-hidden flex flex-col"
      >
        {/* ─────────── Cabeçalho editorial (faixa escura de alto contraste) ─────────── */}
        <header className="shrink-0 bg-sidebar px-5 py-4 text-sidebar-foreground sm:px-7">
          <DialogTitle className="text-xl font-semibold tracking-tight">
            Gerar template desta conta
          </DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm text-sidebar-foreground/70 opacity-80 transition-opacity hover:text-sidebar-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-sidebar-foreground/40">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
          <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1.4fr] shrink-0">
            <div className="space-y-1.5">
              <Label htmlFor="template-nome">Nome</Label>
              <Input
                id="template-nome"
                value={nome}
                maxLength={120}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Rastreamento veicular — padrão"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="template-segmento">Segmento</Label>
              <Input
                id="template-segmento"
                value={segmento}
                maxLength={80}
                onChange={(e) => setSegmento(e.target.value)}
                placeholder="Ex.: Rastreamento veicular"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="template-descricao">Descrição</Label>
              <Textarea
                id="template-descricao"
                value={descricao}
                maxLength={500}
                rows={1}
                className="min-h-[38px]"
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Para que serve este template"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
            <InlineNotice variant="info" className="flex-1 min-w-[280px]">
              Nenhum cliente, contato, negócio, conversa, cobrança ou credencial é incluído no
              arquivo.
            </InlineNotice>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="ghost" onClick={marcarTodos}>
                Marcar tudo
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setModulos([])}>
                Limpar
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1">
            <TemplateModuleSelector
              modules={modulosDisponiveis}
              selectedModules={modulos}
              onToggleModule={alternarModulo}
              selection={selecao}
              onSelectionChange={setSelecao}
              itemsByModule={itensPorModulo}
              loadingModules={modulosCarregando}
              activeModule={moduloAtivo}
              onActiveModuleChange={setModuloAtivo}
            />
          </div>
        </div>


        <DialogFooter className="border-t px-6 py-3 sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {modulos.length} módulo(s) · {totalItens} item(ns) selecionado(s)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmar} disabled={exportarMutation.isPending}>
              {exportarMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Gerar template
            </Button>
          </div>
        </DialogFooter>


      </DialogContent>
    </Dialog>
  );
}

export default TemplateExportDialog;
